'use client';

import { JSX, useState } from 'react';
import { useDebounceCallback } from 'usehooks-ts';

import { tw } from '../../utils';

type Option<T = unknown> = {
  default?: boolean;
  label: string | JSX.Element;
  context: T;
};

interface IProps<T = unknown> {
  onContextChained?: (context: T) => void;
  disabled?: boolean;
  options: Option<T>[];
  debounceMs?: number;
}

export const PillSelector = <T = unknown,>({
  onContextChained,
  disabled,
  options,
  debounceMs = 500,
}: IProps<T>) => {
  const [selectedIndex, setSelectedIndex] = useState(
    options.findIndex((x) => x.default) || 0,
  );

  const contextChanged = useDebounceCallback(
    (option: Option<T>) => {
      if (!onContextChained) return;
      onContextChained(option.context);
    },
    debounceMs,
    {
      trailing: true,
    },
  );

  return (
    <div className={tw('flex h-7 w-[145px] justify-around overflow-clip')}>
      {options.map((x, i) => {
        const selected = selectedIndex === i;

        return (
          <button
            key={i}
            disabled={selected || disabled}
            onClick={() => {
              setSelectedIndex(i);
              contextChanged(x);
            }}
            className={tw(
              selected ? 'bg-white text-black' : 'text-white',
              i == 0 ? 'rounded-l-xl border-l-2' : 'rounded-r-xl border-r-2',
              'flex w-full items-center justify-center',
              'font-fredoka text-[14px] font-semibold',
              'border-b-2 border-t-2 border-white',
              'transition-all duration-150 ease-in-out',
            )}
          >
            {x.label}
          </button>
        );
      })}
    </div>
  );
};
